import { Capacitor } from '@capacitor/core';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, 
  ChevronDown, 
  ChevronUp,
  Eye, 
  Calendar, 
  User, 
  MessageSquare,
  AlertCircle,
  CheckCircle,
  XCircle,
  Plus,
  RefreshCw,
  X,
  ChevronRight,
} from 'lucide-react';
import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import LoadingSpinner from '../../../components/LoadingSpinner';
import { logError } from '../../../services/loggingService';
import { 
  getSupportTickets, 
  createSupportTicket, 
  getTicketStatistics
  
   
} from '../../../services/supportTicketService';
import type {SupportTicket, CreateTicketRequest} from '../../../services/supportTicketService';

const SupportTickets: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<keyof SupportTicket>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(5);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [showTicketDetails, setShowTicketDetails] = useState(false);
  const [statistics, setStatistics] = useState({
    total: 0,
    open: 0,
    in_progress: 0,
    resolved: 0,
    closed: 0
  });

  // Create ticket form state
  const [newTicket, setNewTicket] = useState<CreateTicketRequest>({
    subject: '',
    description: '',
    priority: 'medium',
    category: 'General'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch tickets and statistics
  const fetchTickets = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [ticketsData, statsData] = await Promise.all([
        getSupportTickets(),
        getTicketStatistics()
      ]);
      
      setTickets(ticketsData);
      setStatistics(statsData);
    } catch (err) {
      console.error('Error fetching tickets:', err);
      setError(t('dashboard.support.failedToLoadTickets'));
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchTickets();
  }, []);

  // Generate readable ticket ID
  const generateTicketId = (uuid: string) => {
    const shortId = uuid.slice(0, 8).toUpperCase();
    return `TICK-${shortId}`;
  };

  // Create new ticket
  const handleCreateTicket = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newTicket.subject.trim() || !newTicket.description.trim()) {
      setError(t('dashboard.support.subjectAndDescriptionRequired'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const createdTicket = await createSupportTicket(newTicket);
      setTickets(prev => [createdTicket, ...prev]);
      setNewTicket({
        subject: '',
        description: '',
        priority: 'medium',
        category: 'General'
      });
      setShowCreateForm(false);
      
      // Refresh statistics
      const stats = await getTicketStatistics();
      setStatistics(stats);
    } catch (err) {
      console.error('Error creating ticket:', err);
      setError(t('dashboard.support.failedToCreateTicket'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle sort
  const handleSort = (column: keyof SupportTicket) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  // Filter and sort tickets
  const filteredTickets = useMemo(() => {
    let filtered = tickets.filter(ticket => {
      const ticketId = generateTicketId(ticket.ticket_id);
      const matchesSearch = ticket.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           ticket.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           ticketId.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || ticket.status === statusFilter;
      const matchesPriority = priorityFilter === 'all' || ticket.priority === priorityFilter;

      return matchesSearch && matchesStatus && matchesPriority;
    });

    // Sort tickets
    filtered.sort((a, b) => {
      const aValue = a[sortBy];
      const bValue = b[sortBy];
      
      if (sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    return filtered;
  }, [tickets, searchTerm, statusFilter, priorityFilter, sortBy, sortOrder]);

  // Pagination
  const totalPages = Math.ceil(filteredTickets.length / recordsPerPage);
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const currentTickets = filteredTickets.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleRecordsPerPageChange = (records: number) => {
    setRecordsPerPage(records);
    setCurrentPage(1);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open':
        return <AlertCircle className="w-3.5 h-3.5" aria-hidden />;
      case 'in_progress':
        return <RefreshCw className="w-3.5 h-3.5" aria-hidden />;
      case 'resolved':
        return <CheckCircle className="w-3.5 h-3.5" aria-hidden />;
      case 'closed':
        return <XCircle className="w-3.5 h-3.5" aria-hidden />;
      default:
        return <AlertCircle className="w-3.5 h-3.5" aria-hidden />;
    }
  };

  const getStatusLabel = (status: string) => {
    const keyMap: Record<string, [string, string]> = {
      open: ['dashboard.support.statusOpen', 'Open'],
      in_progress: ['dashboard.support.statusInProgress', 'In progress'],
      pending: ['dashboard.support.statusPending', 'Pending'],
      resolved: ['dashboard.support.statusResolved', 'Resolved'],
      closed: ['dashboard.support.statusClosed', 'Closed'],
    };
    const entry = keyMap[status];
    return entry ? t(entry[0], entry[1]) : status.replace(/_/g, ' ');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'vscode-bg-tertiary/50 vscode-text-accent vscode-border';
      case 'in_progress':
        return 'vscode-bg-tertiary/50 vscode-text-accent vscode-border';
      case 'resolved':
        return 'bg-[var(--vscode-success-bg)] text-[var(--vscode-success)] border-[var(--vscode-success-border)]';
      case 'closed':
        return 'vscode-bg-tertiary/30 vscode-text-tertiary vscode-border';
      default:
        return 'vscode-bg-tertiary/30 vscode-text-tertiary vscode-border';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'low':
        return 'bg-[var(--vscode-success-bg)] text-[var(--vscode-success)] border-[var(--vscode-success-border)]';
      case 'medium':
        return 'vscode-bg-tertiary/50 vscode-text-accent vscode-border';
      case 'high':
        return 'bg-[var(--vscode-error-bg)]/50 vscode-text-accent border-[var(--vscode-error-border)]';
      case 'urgent':
        return 'bg-[var(--vscode-error-bg)] text-[var(--vscode-error)] border-[var(--vscode-error-border)]';
      default:
        return 'bg-gray-500/20 vscode-text-tertiary border-gray-500/30';
    }
  };

  const dateLocaleMap: Record<string, string> = { en: 'en-US', es: 'es-ES', pt: 'pt-BR', ru: 'ru-RU' };
  const localeForDate = dateLocaleMap[i18n.language?.split('-')[0] || ''] ?? i18n.language ?? 'en-US';
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(localeForDate, {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getPriorityLabel = (priority: string) => {
    const keyMap: Record<string, [string, string]> = {
      low: ['dashboard.support.priorityLow', 'Low'],
      medium: ['dashboard.support.priorityMedium', 'Medium'],
      high: ['dashboard.support.priorityHigh', 'High'],
      urgent: ['dashboard.support.priorityUrgent', 'Urgent'],
    };
    const entry = keyMap[priority];
    return entry ? t(entry[0], entry[1]) : priority;
  };

  const categoryToKey: Record<string, [string, string]> = {
    'General': ['dashboard.support.categoryGeneral', 'General'],
    'Bug Report': ['dashboard.support.categoryBugReport', 'Bug Report'],
    'Technical Issue': ['dashboard.support.categoryTechnicalIssue', 'Technical Issue'],
    'Feature Request': ['dashboard.support.categoryFeatureRequest', 'Feature Request'],
    'Performance': ['dashboard.support.categoryPerformance', 'Performance'],
    'UI/UX': ['dashboard.support.categoryUiUx', 'UI/UX'],
  };

  const categoryOptions = useMemo(
    () => Object.entries(categoryToKey).map(([value, [key, def]]) => ({
      value,
      label: t(key, def),
    })),
    [t],
  );

  const getCategoryLabel = (category: string) => {
    const entry = categoryToKey[category];
    return entry ? t(entry[0], entry[1]) : category;
  };

  if (loading) {
    if (Capacitor.isNativePlatform()) return null;
    return (
      <div className="ggj-light-palette ds-support-page flex flex-1 items-center justify-center w-full min-h-0">
        <LoadingSpinner size="lg" showText={false} />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center space-y-4">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto" />
          <div>
            <h3 className="text-lg font-semibold vscode-text-primary mb-2">{t('dashboard.support.errorLoadingTickets')}</h3>
            <p className="vscode-text-secondary mb-4">{error}</p>
            <button
              onClick={fetchTickets}
              className="px-4 py-2 font-semibold rounded-lg transition-opacity hover:opacity-90 text-white dark:border dark:border-[rgba(242,242,243,0.25)] dark:text-[#F2F2F3]"
              style={{ background: 'var(--brand-cta-bg)' }}
            >
              {t('dashboard.support.tryAgain')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── Native card view ─────────────────────────────────────────────── */
  if (Capacitor.isNativePlatform()) {
    const priorityColors: Record<string, { bg: string; text: string }> = {
      low:    { bg: 'rgba(34,197,94,0.12)',  text: '#16a34a' },
      medium: { bg: 'rgba(108,71,255,0.12)', text: 'var(--vscode-primary)' },
      high:   { bg: 'rgba(249,115,22,0.12)', text: '#ea580c' },
      urgent: { bg: 'rgba(239,68,68,0.12)',  text: '#dc2626' },
    };
    const statusColors: Record<string, { bg: string; text: string }> = {
      open:        { bg: 'rgba(59,130,246,0.12)',  text: '#2563eb' },
      in_progress: { bg: 'rgba(249,115,22,0.12)',  text: '#ea580c' },
      resolved:    { bg: 'rgba(34,197,94,0.12)',   text: '#16a34a' },
      closed:      { bg: 'rgba(156,163,175,0.15)', text: '#6b7280' },
    };

    const cardStyle: React.CSSProperties = {
      background: 'var(--vscode-bg-elevated)',
      border: '1px solid var(--vscode-border-primary)',
      borderRadius: 14,
      padding: '14px 16px',
      marginBottom: 10,
    };

    return (
      <div key={i18n.language} style={{ padding: '12px 16px', background: 'var(--vscode-bg-primary)', minHeight: '100%' }}>
        {/* Stats + New Ticket */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 18 }}>
          {[
            { label: t('dashboard.support.total', 'Total'), value: statistics.total },
            { label: t('dashboard.support.open', 'Open'), value: statistics.open },
            { label: t('dashboard.support.resolved', 'Resolved'), value: statistics.resolved },
          ].map(({ label, value }) => (
            <div
              key={label}
              style={{
                flex: 1, background: 'var(--vscode-bg-elevated)', border: '1px solid var(--vscode-border-primary)',
                borderRadius: 12, padding: '10px 8px', textAlign: 'center',
              }}
            >
              <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--vscode-primary)' }}>{value}</div>
              <div style={{ fontSize: 10, color: 'var(--vscode-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>{label}</div>
            </div>
          ))}
          <button
            type="button"
            onClick={() => setShowCreateForm(true)}
            style={{
              flexShrink: 0, width: 44, height: 'auto', borderRadius: 12, border: 'none',
              background: 'var(--brand-cta-bg)', cursor: 'pointer', display: 'flex',
              alignItems: 'center', justifyContent: 'center', color: '#fff',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <Plus size={20} />
          </button>
        </div>

        {/* Error */}
        {error && (
          <div style={{ padding: '10px 14px', background: 'var(--vscode-error-bg)', borderRadius: 10, marginBottom: 12 }}>
            <p style={{ fontSize: 13, color: 'var(--vscode-error)' }}>{error}</p>
          </div>
        )}

        {/* Ticket list */}
        {filteredTickets.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--vscode-text-tertiary)' }}>
            <MessageSquare size={36} style={{ margin: '0 auto 12px', opacity: 0.4 }} />
            <p style={{ fontSize: 14, fontWeight: 600 }}>{t('dashboard.support.noTicketsFound', 'No tickets found')}</p>
            <p style={{ fontSize: 12, marginTop: 4 }}>{t('dashboard.support.noTicketsHintEmpty', 'No support tickets have been created yet')}</p>
          </div>
        ) : (
          filteredTickets.map((ticket) => {
            const sc = statusColors[ticket.status] ?? { bg: 'rgba(156,163,175,0.15)', text: '#6b7280' };
            const pc = priorityColors[ticket.priority] ?? { bg: 'rgba(156,163,175,0.15)', text: '#6b7280' };
            return (
              <button
                key={ticket.id}
                type="button"
                onClick={() => { setSelectedTicket(ticket); setShowTicketDetails(true); }}
                style={{ ...cardStyle, width: '100%', textAlign: 'left', cursor: 'pointer', WebkitTapHighlightColor: 'transparent', display: 'block' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div style={{ flex: 1, minWidth: 0, paddingRight: 8 }}>
                    <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--vscode-text-primary)', marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {ticket.subject}
                    </p>
                    <p style={{ fontSize: 11, color: 'var(--vscode-text-tertiary)' }}>
                      {generateTicketId(ticket.id)} · {getCategoryLabel(ticket.category || 'General')}
                    </p>
                  </div>
                  <ChevronRight size={16} style={{ color: 'var(--vscode-text-tertiary)', flexShrink: 0, marginTop: 2 }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20, background: sc.bg, color: sc.text, textTransform: 'capitalize' }}>
                      {getStatusLabel(ticket.status)}
                    </span>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20, background: pc.bg, color: pc.text, textTransform: 'capitalize' }}>
                      {getPriorityLabel(ticket.priority)}
                    </span>
                  </div>
                  <p style={{ fontSize: 11, color: 'var(--vscode-text-tertiary)' }}>
                    {formatDate(ticket.created_at)}
                  </p>
                </div>
              </button>
            );
          })
        )}

        {/* Create Ticket Modal — full screen on native */}
        {showCreateForm && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'var(--vscode-bg-primary)', display: 'flex', flexDirection: 'column' }}>
            <div style={{ background: 'var(--brand-popup-header)', paddingTop: 'max(calc(var(--safe-area-top, env(safe-area-inset-top, 0px)) + 8px), 40px)', paddingBottom: 22, paddingLeft: 20, paddingRight: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button type="button" onClick={() => setShowCreateForm(false)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 10, width: 34, height: 34, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                  <X size={16} />
                </button>
                <h1 style={{ fontSize: 18, fontWeight: 800, color: '#fff', margin: 0 }}>{t('dashboard.support.newTicket', 'New Ticket')}</h1>
              </div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 16px 32px' }}>
              <form onSubmit={handleCreateTicket} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--vscode-text-secondary)', display: 'block', marginBottom: 6 }}>{t('dashboard.support.subjectLabel', 'Subject')} *</label>
                  <input
                    type="text"
                    value={newTicket.subject}
                    onChange={(e) => setNewTicket(p => ({ ...p, subject: e.target.value }))}
                    placeholder={t('dashboard.support.subjectPlaceholder', 'Brief description of the issue')}
                    style={{ width: '100%', padding: '12px 14px', borderRadius: 12, border: '1px solid var(--vscode-border-primary)', background: 'var(--vscode-bg-elevated)', color: 'var(--vscode-text-primary)', fontSize: 14, boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--vscode-text-secondary)', display: 'block', marginBottom: 6 }}>{t('dashboard.support.descriptionLabel', 'Description')} *</label>
                  <textarea
                    value={newTicket.description}
                    onChange={(e) => setNewTicket(p => ({ ...p, description: e.target.value }))}
                    placeholder={t('dashboard.support.descriptionPlaceholder', 'Detailed description of the issue')}
                    rows={4}
                    style={{ width: '100%', padding: '12px 14px', borderRadius: 12, border: '1px solid var(--vscode-border-primary)', background: 'var(--vscode-bg-elevated)', color: 'var(--vscode-text-primary)', fontSize: 14, resize: 'none', boxSizing: 'border-box' }}
                  />
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--vscode-text-secondary)', display: 'block', marginBottom: 6 }}>{t('dashboard.support.priorityLabel', 'Priority')}</label>
                    <select
                      value={newTicket.priority}
                      onChange={(e) => setNewTicket(p => ({ ...p, priority: e.target.value as CreateTicketRequest['priority'] }))}
                      style={{ width: '100%', padding: '12px 14px', borderRadius: 12, border: '1px solid var(--vscode-border-primary)', background: 'var(--vscode-bg-elevated)', color: 'var(--vscode-text-primary)', fontSize: 14 }}
                    >
                      <option value="low">{t('dashboard.support.priorityLow', 'Low')}</option>
                      <option value="medium">{t('dashboard.support.priorityMedium', 'Medium')}</option>
                      <option value="high">{t('dashboard.support.priorityHigh', 'High')}</option>
                      <option value="urgent">{t('dashboard.support.priorityUrgent', 'Urgent')}</option>
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--vscode-text-secondary)', display: 'block', marginBottom: 6 }}>{t('dashboard.support.categoryLabel', 'Category')}</label>
                    <select
                      value={newTicket.category}
                      onChange={(e) => setNewTicket(p => ({ ...p, category: e.target.value }))}
                      style={{ width: '100%', padding: '12px 14px', borderRadius: 12, border: '1px solid var(--vscode-border-primary)', background: 'var(--vscode-bg-elevated)', color: 'var(--vscode-text-primary)', fontSize: 14 }}
                    >
                      {categoryOptions.map(({ value, label }) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {error && <p style={{ fontSize: 13, color: 'var(--vscode-error)' }}>{error}</p>}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{ padding: '14px', borderRadius: 14, border: 'none', background: 'var(--brand-cta-bg)', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer', marginTop: 4 }}
                >
                  {isSubmitting ? t('dashboard.support.submitting', 'Submitting…') : t('dashboard.support.submitTicket', 'Submit Ticket')}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Ticket Detail — full screen on native */}
        {showTicketDetails && selectedTicket && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'var(--vscode-bg-primary)', display: 'flex', flexDirection: 'column' }}>
            <div style={{ background: 'var(--brand-popup-header)', paddingTop: 'max(calc(var(--safe-area-top, env(safe-area-inset-top, 0px)) + 8px), 40px)', paddingBottom: 22, paddingLeft: 20, paddingRight: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button type="button" onClick={() => setShowTicketDetails(false)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 10, width: 34, height: 34, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                  <ChevronRight size={16} style={{ transform: 'rotate(180deg)' }} />
                </button>
                <h1 style={{ fontSize: 18, fontWeight: 800, color: '#fff', margin: 0 }}>{generateTicketId(selectedTicket.id)}</h1>
              </div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
              <h2 style={{ fontSize: 17, fontWeight: 700, color: 'var(--vscode-text-primary)', marginBottom: 12 }}>{selectedTicket.subject}</h2>
              <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 20, background: (statusColors[selectedTicket.status] ?? { bg: '' }).bg, color: (statusColors[selectedTicket.status] ?? { text: '' }).text, textTransform: 'capitalize' }}>
                  {getStatusLabel(selectedTicket.status)}
                </span>
                <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 20, background: (priorityColors[selectedTicket.priority] ?? { bg: '' }).bg, color: (priorityColors[selectedTicket.priority] ?? { text: '' }).text, textTransform: 'capitalize' }}>
                  {getPriorityLabel(selectedTicket.priority)}
                </span>
              </div>
              <p style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--vscode-text-secondary)', marginBottom: 16 }}>{selectedTicket.description}</p>
              <div style={{ fontSize: 12, color: 'var(--vscode-text-tertiary)', display: 'flex', flexDirection: 'column', gap: 4 }}>
                <p>{t('dashboard.support.detailCategory', 'Category')}: {getCategoryLabel(selectedTicket.category || 'General')}</p>
                <p>{t('dashboard.support.detailCreated', 'Created')}: {formatDate(selectedTicket.created_at)}</p>
                {selectedTicket.updated_at && <p>{t('dashboard.support.detailUpdated', 'Updated')}: {formatDate(selectedTicket.updated_at)}</p>}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div key={i18n.language} className="ggj-light-palette ds-support-page flex flex-1 min-h-0 flex-col gap-3 overflow-y-auto overflow-x-hidden px-3 sm:px-6 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] min-w-0">
      {/* KPI strip + primary action */}
      <div className="ds-support-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 flex-shrink-0 min-w-0">
        <div className="ds-support-kpi-grid w-full sm:w-auto">
          <div className="ds-support-kpi">
            <span className="ds-support-kpi__value t-num">{statistics.total}</span>
            <span className="ds-support-kpi__label t-meta">{t('dashboard.support.total')}</span>
          </div>
          <div className="ds-support-kpi">
            <span className="ds-support-kpi__value t-num">{statistics.open}</span>
            <span className="ds-support-kpi__label t-meta">{t('dashboard.support.open')}</span>
          </div>
          <div className="ds-support-kpi ds-support-kpi--ok">
            <span className="ds-support-kpi__value t-num">{statistics.resolved}</span>
            <span className="ds-support-kpi__label t-meta">{t('dashboard.support.resolved')}</span>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setShowCreateForm(true)}
          className="ds-btn ds-btn-primary min-h-[44px] w-full sm:w-auto touch-manipulation shrink-0 self-stretch sm:self-center"
        >
          <Plus className="w-4 h-4" aria-hidden />
          <span>{t('dashboard.support.newTicket')}</span>
        </button>
      </div>

      {/* Search + filters */}
      <div className="ds-support-toolbar flex-shrink-0">
        <div className="flex flex-col gap-2">
          <div className="flex-1 relative min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ds-support-toolbar__icon" aria-hidden />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('dashboard.support.searchPlaceholder')}
              className="ds-support-toolbar__input w-full pl-10 pr-4 py-2.5 rounded-lg text-sm min-h-[44px]"
            />
          </div>
          <div className="grid grid-cols-2 sm:flex sm:flex-row gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="ds-support-toolbar__select w-full sm:w-40 py-2.5 px-3 rounded-lg text-sm min-h-[44px]"
            aria-label={t('dashboard.support.allStatus')}
          >
            <option value="all">{t('dashboard.support.allStatus')}</option>
            <option value="open">{t('dashboard.support.statusOpen')}</option>
            <option value="in_progress">{t('dashboard.support.statusInProgress')}</option>
            <option value="resolved">{t('dashboard.support.statusResolved')}</option>
            <option value="closed">{t('dashboard.support.statusClosed')}</option>
          </select>
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="ds-support-toolbar__select w-full sm:w-40 py-2.5 px-3 rounded-lg text-sm min-h-[44px]"
            aria-label={t('dashboard.support.allPriority')}
          >
            <option value="all">{t('dashboard.support.allPriority')}</option>
            <option value="low">{t('dashboard.support.priorityLow')}</option>
            <option value="medium">{t('dashboard.support.priorityMedium')}</option>
            <option value="high">{t('dashboard.support.priorityHigh')}</option>
            <option value="urgent">{t('dashboard.support.priorityUrgent')}</option>
          </select>
          </div>
        </div>
      </div>

      {/* Ticket table */}
      <div className="ds-support-table-shell flex flex-col flex-shrink-0 min-w-0">
        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <div className="min-w-[720px]">
            <div className="ds-support-table__head grid grid-cols-12 gap-3 px-3 py-2">
              <div className="col-span-1 t-label">{t('dashboard.support.serialNo')}</div>
              <div className="col-span-3">
                <button type="button" onClick={() => handleSort('subject')} className="ds-support-table__sort t-label">
                  <span>{t('dashboard.support.subject')}</span>
                  {sortBy === 'subject' ? (sortOrder === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />) : <ChevronDown className="w-3 h-3 opacity-30" />}
                </button>
              </div>
              <div className="col-span-2">
                <button type="button" onClick={() => handleSort('status')} className="ds-support-table__sort t-label">
                  <span>{t('dashboard.support.status')}</span>
                  {sortBy === 'status' ? (sortOrder === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />) : <ChevronDown className="w-3 h-3 opacity-30" />}
                </button>
              </div>
              <div className="col-span-1">
                <button type="button" onClick={() => handleSort('priority')} className="ds-support-table__sort t-label">
                  <span>{t('dashboard.support.priority')}</span>
                  {sortBy === 'priority' ? (sortOrder === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />) : <ChevronDown className="w-3 h-3 opacity-30" />}
                </button>
              </div>
              <div className="col-span-2 t-label">{t('dashboard.support.category')}</div>
              <div className="col-span-2">
                <button type="button" onClick={() => handleSort('created_at')} className="ds-support-table__sort t-label">
                  <span>{t('dashboard.support.created')}</span>
                  {sortBy === 'created_at' ? (sortOrder === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />) : <ChevronDown className="w-3 h-3 opacity-30" />}
                </button>
              </div>
              <div className="col-span-1 t-label text-center">{t('dashboard.support.actions')}</div>
            </div>

            {currentTickets.length === 0 ? (
              <div className="ds-support-empty flex flex-col items-center justify-center py-16 px-4">
                <MessageSquare className="w-10 h-10 mb-3 opacity-40" aria-hidden />
                <p className="t-card-title">{t('dashboard.support.noTicketsFound')}</p>
                <p className="t-meta mt-1 text-center max-w-md">
                  {searchTerm || statusFilter !== 'all' || priorityFilter !== 'all'
                    ? t('dashboard.support.noTicketsHintFilter')
                    : t('dashboard.support.noTicketsHintEmpty')}
                </p>
              </div>
            ) : (
              currentTickets.map((ticket, index) => {
                const serialNumber = startIndex + index + 1;
                return (
                  <motion.div
                    key={ticket.ticket_id}
                    className="ds-support-table__row grid grid-cols-12 gap-3 items-center px-3 py-2.5"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.15 }}
                  >
                    <div className="col-span-1 t-meta t-num">{serialNumber}</div>
                    <div className="col-span-3 min-w-0">
                      <p className="t-body truncate" title={ticket.subject}>{ticket.subject}</p>
                      <p className="t-meta truncate mt-px">{ticket.description.substring(0, 72)}{ticket.description.length > 72 ? '…' : ''}</p>
                    </div>
                    <div className="col-span-2">
                      <span className={`ds-support-pill ds-support-pill--status-${ticket.status}`}>
                        {getStatusIcon(ticket.status)}
                        {getStatusLabel(ticket.status)}
                      </span>
                    </div>
                    <div className="col-span-1">
                      <span className={`ds-support-pill ds-support-pill--priority-${ticket.priority}`}>
                        {getPriorityLabel(ticket.priority)}
                      </span>
                    </div>
                    <div className="col-span-2 t-body truncate">{getCategoryLabel(ticket.category)}</div>
                    <div className="col-span-2">
                      <span className="t-meta inline-flex items-center gap-1 whitespace-nowrap">
                        <Calendar className="w-3 h-3 shrink-0 opacity-70" aria-hidden />
                        {formatDate(ticket.created_at)}
                      </span>
                    </div>
                    <div className="col-span-1 flex justify-center">
                      <button
                        type="button"
                        onClick={() => { setSelectedTicket(ticket); setShowTicketDetails(true); }}
                        className="ds-support-row-action"
                        title={t('dashboard.support.viewDetails')}
                        aria-label={t('dashboard.support.viewDetails')}
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </div>
                  </motion.div>
                );
              })
            )}
          </div>
        </div>

        {/* Mobile card list */}
        <div className="md:hidden">
          {currentTickets.length === 0 ? (
            <div className="ds-support-empty flex flex-col items-center justify-center py-12 px-4">
              <MessageSquare className="w-10 h-10 mb-3 opacity-40" aria-hidden />
              <p className="t-card-title">{t('dashboard.support.noTicketsFound')}</p>
              <p className="t-meta mt-1 text-center max-w-md">
                {searchTerm || statusFilter !== 'all' || priorityFilter !== 'all'
                  ? t('dashboard.support.noTicketsHintFilter')
                  : t('dashboard.support.noTicketsHintEmpty')}
              </p>
            </div>
          ) : (
            currentTickets.map((ticket, index) => {
              const serialNumber = startIndex + index + 1;
              return (
                <motion.div
                  key={ticket.ticket_id}
                  className="ds-support-mobile-row px-3 py-3"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.15 }}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1.5">
                        <span className="t-meta t-num">#{serialNumber}</span>
                        <span className={`ds-support-pill ds-support-pill--status-${ticket.status}`}>
                          {getStatusLabel(ticket.status)}
                        </span>
                        <span className={`ds-support-pill ds-support-pill--priority-${ticket.priority}`}>
                          {getPriorityLabel(ticket.priority)}
                        </span>
                      </div>
                      <p className="t-body break-words">{ticket.subject}</p>
                      <p className="t-meta mt-0.5 line-clamp-2">{ticket.description}</p>
                      <p className="t-meta mt-1 inline-flex items-center gap-1">
                        <Calendar className="w-3 h-3 shrink-0 opacity-70" aria-hidden />
                        {formatDate(ticket.created_at)}
                        <span aria-hidden>·</span>
                        {getCategoryLabel(ticket.category)}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => { setSelectedTicket(ticket); setShowTicketDetails(true); }}
                      className="ds-support-row-action shrink-0"
                      title={t('dashboard.support.viewDetails')}
                      aria-label={t('dashboard.support.viewDetails')}
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </div>
                </motion.div>
              );
            })
          )}
        </div>

        {/* Pagination */}
        <div className="ds-support-table__pagination px-3 sm:px-4 py-3 flex flex-wrap items-center justify-between gap-3">
          <span className="t-meta">
            {t('dashboard.support.showingRange', { start: filteredTickets.length ? startIndex + 1 : 0, end: Math.min(endIndex, filteredTickets.length), total: filteredTickets.length })}
          </span>
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 t-meta">
              <span>{t('dashboard.support.showItemsPerPage')}</span>
              <select
                value={recordsPerPage}
                onChange={(e) => handleRecordsPerPageChange(Number(e.target.value))}
                className="ds-support-toolbar__select py-1.5 px-2 rounded text-sm min-w-[4rem]"
                aria-label={t('dashboard.support.itemsPerPageAria')}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="ds-btn ds-btn-secondary ds-btn-sm min-h-[44px] min-w-[44px] touch-manipulation disabled:opacity-50"
              >
                {t('dashboard.support.prev')}
              </button>
              <span className="t-meta min-w-[5rem] text-center">
                {t('dashboard.support.pageOf', { current: currentPage, total: Math.max(1, totalPages) })}
              </span>
              <button
                type="button"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
                className="ds-btn ds-btn-secondary ds-btn-sm min-h-[44px] min-w-[44px] touch-manipulation disabled:opacity-50"
              >
                {t('dashboard.support.next')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Create Ticket Modal */}
      <AnimatePresence>
        {showCreateForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex flex-col sm:items-center sm:justify-center bg-black/50 sm:p-4"
            onClick={() => setShowCreateForm(false)}
          >
            <motion.div
              initial={{ scale: 0.98, opacity: 0, y: 16 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.98, opacity: 0, y: 16 }}
              className="vscode-bg-secondary flex flex-col w-full h-full sm:h-auto sm:max-w-md sm:rounded-lg p-4 sm:p-6 sm:mx-4 border-0 sm:border vscode-border overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4 shrink-0">
                <h3 className="text-lg font-semibold vscode-text-primary">{t('dashboard.support.createNewTicket')}</h3>
                <button
                  onClick={() => setShowCreateForm(false)}
                  className="min-h-[44px] min-w-[44px] flex items-center justify-center vscode-text-tertiary hover:vscode-text-primary transition-colors touch-manipulation"
                  aria-label={t('dashboard.support.closeAria')}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleCreateTicket} className="space-y-4 flex-1">
                <div>
                  <label className="block text-sm font-medium vscode-text-secondary mb-2">{t('dashboard.support.subjectLabel')}</label>
                  <input
                    type="text"
                    value={newTicket.subject}
                    onChange={(e) => setNewTicket(prev => ({ ...prev, subject: e.target.value }))}
                    className="w-full px-3 py-2 vscode-input rounded-lg vscode-text-primary focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    placeholder={t('dashboard.support.subjectPlaceholder')}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium vscode-text-secondary mb-2">{t('dashboard.support.descriptionLabel')}</label>
                  <textarea
                    value={newTicket.description}
                    onChange={(e) => setNewTicket(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 vscode-input rounded-lg vscode-text-primary focus:outline-none focus:ring-2 focus:ring-cyan-500 h-24 resize-none"
                    placeholder={t('dashboard.support.descriptionPlaceholder')}
                    required
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium vscode-text-secondary mb-2">{t('dashboard.support.priorityLabel')}</label>
                    <select
                      value={newTicket.priority}
                      onChange={(e) => setNewTicket(prev => ({ ...prev, priority: e.target.value as any }))}
                      className="w-full px-3 py-2 vscode-input rounded-lg vscode-text-primary focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    >
                      <option value="low">{t('dashboard.support.priorityLow')}</option>
                      <option value="medium">{t('dashboard.support.priorityMedium')}</option>
                      <option value="high">{t('dashboard.support.priorityHigh')}</option>
                      <option value="urgent">{t('dashboard.support.priorityUrgent')}</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium vscode-text-secondary mb-2">{t('dashboard.support.categoryLabel')}</label>
                    <select
                      value={newTicket.category}
                      onChange={(e) => setNewTicket(prev => ({ ...prev, category: e.target.value }))}
                      className="w-full px-3 py-2 vscode-input rounded-lg vscode-text-primary focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    >
                      <option value="General">{t('dashboard.support.categoryGeneral')}</option>
                      <option value="Bug Report">{t('dashboard.support.categoryBugReport')}</option>
                      <option value="Technical Issue">{t('dashboard.support.categoryTechnicalIssue')}</option>
                      <option value="Feature Request">{t('dashboard.support.categoryFeatureRequest')}</option>
                      <option value="Performance">{t('dashboard.support.categoryPerformance')}</option>
                      <option value="UI/UX">{t('dashboard.support.categoryUiUx')}</option>
                    </select>
                  </div>
                </div>

                <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 sm:gap-3 pt-4 mt-auto">
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(false)}
                    className="w-full sm:w-auto min-h-[44px] px-4 py-2 vscode-text-tertiary hover:vscode-text-primary transition-colors touch-manipulation"
                  >
                    {t('dashboard.support.cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2 min-h-[44px] font-semibold rounded-lg transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed text-white dark:border dark:border-[rgba(242,242,243,0.25)] dark:text-[#F2F2F3] touch-manipulation"
                    style={{ background: 'var(--brand-cta-bg)' }}
                  >
                    {isSubmitting ? (
                      <>
                        <LoadingSpinner size="xs" showText={false} />
                        <span>{t('dashboard.support.creating')}</span>
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4" />
                        <span>{t('dashboard.support.createTicket')}</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Ticket Details Modal */}
      <AnimatePresence>
        {showTicketDetails && selectedTicket && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex flex-col sm:items-center sm:justify-center bg-black/50 sm:p-4"
            onClick={() => setShowTicketDetails(false)}
          >
            <motion.div
              initial={{ scale: 0.98, opacity: 0, y: 16 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.98, opacity: 0, y: 16 }}
              className="vscode-bg-secondary flex flex-col w-full h-full sm:h-auto sm:max-w-2xl sm:rounded-lg p-4 sm:p-6 sm:mx-4 overflow-y-auto border-0 sm:border vscode-border"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-3 mb-4 sm:mb-6">
                <div className="min-w-0 flex-1">
                  <h3 className="text-lg sm:text-xl font-semibold vscode-text-primary break-words">{selectedTicket.subject}</h3>
                  <p className="vscode-text-accent text-sm font-mono mt-0.5">{generateTicketId(selectedTicket.ticket_id)}</p>
                </div>
                <button
                  onClick={() => setShowTicketDetails(false)}
                  className="flex-shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center vscode-text-tertiary hover:vscode-text-primary transition-colors touch-manipulation"
                  aria-label={t('dashboard.support.closeAria')}
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Ticket Info: stack on mobile, two columns on sm+ */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium vscode-text-tertiary">{t('dashboard.support.detailStatus')}</label>
                    <div className="flex items-center space-x-2 mt-1">
                      {getStatusIcon(selectedTicket.status)}
                      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(selectedTicket.status)}`}>
                        {getStatusLabel(selectedTicket.status)}
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium vscode-text-tertiary">{t('dashboard.support.detailPriority')}</label>
                    <div className="mt-1">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getPriorityColor(selectedTicket.priority)}`}>
                        {getPriorityLabel(selectedTicket.priority)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium vscode-text-tertiary">{t('dashboard.support.detailCategory')}</label>
                    <p className="vscode-text-primary mt-1">{getCategoryLabel(selectedTicket.category)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium vscode-text-tertiary">{t('dashboard.support.detailCreated')}</label>
                    <p className="vscode-text-primary mt-1">{formatDate(selectedTicket.created_at)}</p>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="mb-6">
                <label className="text-sm font-medium vscode-text-tertiary">{t('dashboard.support.detailDescription')}</label>
                <div className="mt-2 p-4 vscode-bg-tertiary/50 rounded-lg border vscode-border">
                  <p className="vscode-text-primary whitespace-pre-wrap">{selectedTicket.description}</p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end pt-4 border-t vscode-border mt-auto">
                <button
                  onClick={() => setShowTicketDetails(false)}
                  className="w-full sm:w-auto min-h-[44px] px-4 py-2 vscode-text-tertiary hover:vscode-text-primary transition-colors touch-manipulation"
                >
                  {t('dashboard.support.close')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SupportTickets;